import { useState } from "react";
import { ImageOff } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ClubhouseAdKitImageDto } from "entities/responses/adkitImage";
import {
  formatImageSize,
  formatImageUsage,
  imageDisplayName,
} from "./imageLibraryViewModel";

interface ImageCardProps {
  image: ClubhouseAdKitImageDto;
  usedIn: string[] | undefined;
  selected: boolean;
  onSelect: () => void;
}

export function ImagePreview({
  image,
  className,
}: {
  image: ClubhouseAdKitImageDto;
  className?: string;
}) {
  // Some library URLs 404; show the placeholder rather than a broken-image icon.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const url = image.imageUrl;
  return url && failedUrl !== url ? (
    // contain, not cover: cropping an ad here would misrepresent what the sim shows
    <img
      src={url}
      alt=""
      onError={() => setFailedUrl(url)}
      className={cn("w-full rounded-lg bg-muted object-contain", className)}
    />
  ) : (
    <div
      className={cn(
        "flex w-full items-center justify-center rounded-lg bg-muted",
        className,
      )}
    >
      <ImageOff className="size-5 text-text-muted" aria-hidden="true" />
    </div>
  );
}

export function ImageCard({
  image,
  usedIn,
  selected,
  onSelect,
}: ImageCardProps) {
  const size = formatImageSize(image);
  const inUse = (usedIn?.length ?? 0) > 0;

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        "flex flex-col gap-2 rounded-2xl border bg-surface p-3 text-left transition-colors hover:border-ink/40",
        selected ? "border-primary ring-1 ring-primary" : "border-border",
      )}
    >
      <ImagePreview image={image} className="h-22.5" />
      <span className="poppins truncate text-sm font-semibold text-ink">
        {imageDisplayName(image)}
      </span>
      {size && <span className="text-xs text-text-muted">{size}</span>}
      <span
        className={cn(
          "text-xs font-semibold",
          image.deleted
            ? "text-text-muted"
            : inUse
              ? "text-good"
              : "text-text-muted",
        )}
      >
        {image.deleted ? "Deleted" : formatImageUsage(usedIn)}
      </span>
    </button>
  );
}
